import { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';

/* ── Currency symbols ── */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '\u20AC', GBP: '\u00A3', CHF: 'CHF', AED: 'AED',
  RUB: '\u20BD', BTC: '\u20BF', ETH: '\u039E', USDT: '\u20AE', USDC: '\u20AE',
};

/* ── Types ── */
interface FxRates {
  [ccy: string]: number; // rates per 1 USD (OXR base)
}

interface CurrencyContextType {
  /** The user-selected display currency (persisted in localStorage) */
  baseCurrency: string;
  /** Update the display currency */
  setBaseCurrency: (ccy: string) => void;
  /** Symbol for the current base currency */
  symbol: string;
  /** Get symbol for any currency */
  symbolFor: (ccy: string) => string;
  /** Convert an amount from `fromCcy` to the display base currency */
  convert: (amount: number, fromCcy: string) => number;
  /** Convert between any two currencies */
  convertBetween: (amount: number, fromCcy: string, toCcy: string) => number;
  /** Format amount in base currency with symbol: "€12,345" */
  fmt: (amount: number, fromCcy?: string, opts?: { compact?: boolean }) => string;
  /** Whether FX rates are loaded */
  ratesLoaded: boolean;
  /** Currencies we can actually convert to, from the live rate table */
  availableCurrencies: string[];
}

const CurrencyContext = createContext<CurrencyContextType | null>(null);

/* ── Provider ── */
export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [baseCurrency, setBaseCurrencyState] = useState<string>(
    () => localStorage.getItem('fin_base_currency') || 'EUR'
  );
  const [rates, setRates] = useState<FxRates | null>(null);

  // Fetch rates on mount
  useEffect(() => {
    const token = localStorage.getItem('fin_auth_token');
    fetch('/api/fin/fx-rates', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.rates) setRates(data.rates);
      })
      .catch(() => {});
  }, []);

  const setBaseCurrency = useCallback((ccy: string) => {
    setBaseCurrencyState(ccy);
    localStorage.setItem('fin_base_currency', ccy);
  }, []);

  const symbolFor = useCallback((ccy: string) => CURRENCY_SYMBOLS[ccy] || ccy, []);

  const convertBetween = useCallback((amount: number, fromCcy: string, toCcy: string): number => {
    if (!rates || fromCcy === toCcy) return amount;
    // OXR rates are per 1 USD
    // To convert: amount_in_from → USD → to
    // amount_usd = amount / rates[fromCcy]   (if fromCcy is USD, rate is 1)
    // amount_to  = amount_usd * rates[toCcy]
    const fromRate = fromCcy === 'USD' ? 1 : rates[fromCcy];
    const toRate = toCcy === 'USD' ? 1 : rates[toCcy];
    if (!fromRate || !toRate) return amount; // can't convert, return as-is
    return (amount / fromRate) * toRate;
  }, [rates]);

  const convert = useCallback((amount: number, fromCcy: string): number => {
    return convertBetween(amount, fromCcy, baseCurrency);
  }, [convertBetween, baseCurrency]);

  const fmt = useCallback((amount: number, fromCcy?: string, opts?: { compact?: boolean }): string => {
    const converted = fromCcy ? convert(amount, fromCcy) : amount;
    const sym = CURRENCY_SYMBOLS[baseCurrency] || baseCurrency;
    if (opts?.compact && Math.abs(converted) >= 1_000_000) {
      return `${sym}${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(converted)}`;
    }
    if (opts?.compact && Math.abs(converted) >= 1000) {
      return `${sym}${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(converted)}`;
    }
    return `${sym}${new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(converted)}`;
  }, [convert, baseCurrency]);

  // Only offer currencies the live rate table actually carries. EUR and USD
  // are always present: USD is the OXR base, EUR is the reporting currency.
  const availableCurrencies = useMemo(() => {
    const preferred = ['EUR', 'USD', 'GBP', 'CHF', 'AED'];
    if (!rates) return ['EUR', 'USD'];
    const have = preferred.filter(c => c === 'USD' || rates[c]);
    return have.length ? have : ['EUR', 'USD'];
  }, [rates]);

  const value = useMemo<CurrencyContextType>(() => ({
    baseCurrency,
    setBaseCurrency,
    symbol: CURRENCY_SYMBOLS[baseCurrency] || baseCurrency,
    symbolFor,
    convert,
    convertBetween,
    fmt,
    ratesLoaded: rates !== null,
    availableCurrencies,
  }), [baseCurrency, setBaseCurrency, symbolFor, convert, convertBetween, fmt, rates, availableCurrencies]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

/* ── Hook ── */
export function useCurrency(): CurrencyContextType {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}
